// Copyright 2025-2026 - Hewlett Packard Enterprise Company
import { lazy } from 'react';

// When adding new routes, ensure that longer matches are inserted in front of shorter matches
// For example, /analytics/carbonFootprint should come before /analytics
// The order matters, or else you'll see weird bugs.

/*
 * DO NOT DIRECTLY IMPORT PAGES - use react's lazy import
 *
 * In order to take advantage of module splitting, the hint needs to be provided
 * to the bundler that splits can happen at the page level by utilizing lazy imports.
 * This will keep the main bundle small so the app loads faster, and then the
 * additional pages can be loaded lazily as the user visits those pages.
 *
 * component - the lazily loaded page import
 * helpTopic - the support topic index to display in contextual help for this page
 * ldFlags - list of LD flags to || together. If you need more complex logic, set
 *   the complexLDFlag attribute to true and manually add the route to Routes and
 *   the help topic resolution to Help
 * complexLDFlag - for more complex scenarios where the route shouldn't be automatically
 *   added to the routes. When cleaning up the LD flags, remove this attribute
 *   YOU MUST MANUALLY ADD your route to Routes.js and topic resolution to Help.js
 *   There are examples of how to do this inline in both files.
 * All other attributes are passed through to the Route component as is.
 */

// temporarily adding a default topic for routes to use that didn't configure one
// new routes should work with Susan to get an appropriate topic ID
const DEFAULT_TOPIC = 'com-404-tt';

const ErrorPage = lazy(() => import('../components/ErrorPage/ErrorPage'));
const ReportsPage = lazy(() => import('../components/ReportsPage/ReportsPage'));

const ROUTE_CONFIG = {
  '/activity': {
    component: lazy(() => import('../components/ActivityPage/ActivityPage')),
    helpTopic: 'com-activity-tt',
  },
  '/ahsLogAnalyzer': {
    component: lazy(
      () => import('../components/AHSLogAnalyzer/AHSLogAnalyzerPage'),
    ),
    helpTopic: 'com-ahs-analyzer-tt',
    ldFlags: [
      'T_20240410_30981_AHS_FILE_UPLOAD_UI_BOOL',
      'T_20241120_4246_AHS_CSV_INVENTORY_BOOL',
    ],
  },
  '/analytics/carbonFootprint': {
    component: lazy(
      () =>
        import(
          '../components/SustainabilityAnalysisPageV2/SustainabilityAnalysisPageV2'
        ),
    ),
    helpTopic: 'com-sustainability-tt',
    isSessionStorageEnabledForPage: true,
  },
  '/analytics/carbonFootprintDetail/:id': {
    component: lazy(
      () =>
        import(
          '../components/SustainabilityAnalysisDetailPage/SustainabilityAnalysisDetailPage'
        ),
    ),
    helpTopic: 'com-sustainability-tt',
    isSessionStorageEnabledForPage: true,
  },
  '/analytics/serverUtilizationDetail/:id': {
    component: lazy(
      () =>
        import(
          '../components/ServerUtilizationDetailPage/ServerUtilizationDetailPage'
        ),
    ),
    helpTopic: 'com-server-utilization-tt',
    isSessionStorageEnabledForPage: true,
  },
  '/analytics/serverUtilization': {
    component: lazy(
      () =>
        import(
          '../components/ServerUtilizationAnalysisPage/ServerUtilizationAnalysisPage'
        ),
    ),
    helpTopic: 'com-server-utilization-tt',
    isSessionStorageEnabledForPage: true,
  },
  '/analytics': {
    component: ReportsPage,
    helpTopic: 'com-analytics-tt',
  },
  '/applianceDetail/:id': {
    component: lazy(
      () => import('../components/ApplianceDetailPage/ApplianceDetailPage'),
    ),
    helpTopic: 'com-appliance-detail-tt',
  },
  '/appliances': {
    component: lazy(
      () => import('../components/AppliancesPage/AppliancesPage'),
    ),
    helpTopic: 'com-appliances-tt',
  },
  '/approvalPolicy': {
    component: lazy(
      () => import('../components/ApprovalPolicyPage/ApprovalPolicyPage'),
    ),
    helpTopic: 'com-approval-tt',
  },
  '/arubaCentral': {
    component: lazy(
      () =>
        import(
          '../components/ArubaCentralIntegration/ArubaCentralIntegrationPage'
        ),
    ),
    helpTopic: 'com-aruba-tt',
  },
  '/dsccIntegration': {
    component: lazy(
      () => import('../components/DsccIntegration/DsccIntegrationPage'),
    ),
    helpTopic: 'com-dscc-tt',
  },
  '/emailNotification': {
    component: lazy(
      () => import('../components/EmailNotificationPage/EmailNotificationPage'),
    ),
    helpTopic: 'com-email-notification-by-instance-tt',
  },
  '/error/:code': {
    component: ErrorPage,
    helpTopic: 'com-error-tt',
    isUnauthenticated: true,
  },
  '/firmwareUpdateReport/:id': {
    component: lazy(
      () =>
        import('../components/FirmwareUpdateReport/FirmwareUpdateReportPage'),
    ),
    helpTopic: 'com-firmware-report-tt',
  },
  '/downloadFirmwareReport/:id': {
    component: lazy(
      () =>
        import(
          '../components/DownloadBaselineReport/DownloadBaselineReportPage'
        ),
    ),
    helpTopic: 'com-firmware-report-tt',
  },
  '/firmware': {
    component: lazy(() => import('../components/Firmware/FirmwarePageRouter')),
    helpTopic: 'com-firmware-tt',
    exact: true,
  },
  '/servers/firmware': {
    component: lazy(() => import('../components/Firmware/FirmwarePage')),
    helpTopic: 'com-firmware-tt',
    exact: true,
    ldFlags: ['T_20250106_98741_SUM_MODERNISATION_CFM_UI_BOOL'],
  },
  '/groupComplianceIssue/:id': {
    component: lazy(
      () =>
        import(
          '../components/GroupFirmwareCompliance/GroupComplianceIssuePage'
        ),
    ),
    helpTopic: 'com-group-compliance-issue-tt',
  },
  '/groupComplianceIssues/:id': {
    component: lazy(
      () => import('../components/GroupDetailPage/GroupSettingCompDetailPage'),
    ),
    helpTopic: 'com-group-compliance-issues-tt',
  },
  '/groupDetail/:id': {
    component: lazy(
      () => import('../components/GroupDetailPage/GroupDetailPage'),
    ),
    helpTopic: 'com-group-details-page-tt',
  },
  '/groupExternalStorageCompliance/:id': {
    component: lazy(
      () =>
        import(
          '../components/GroupExternalStorageCompliance/GroupExternalStorageCompliance'
        ),
    ),
    helpTopic: 'com-group-compliance-storage-issue-tt',
  },
  '/groupIloDetailIssue/:id': {
    component: lazy(
      () =>
        import('../components/GroupFirmwareCompliance/GroupIloDetailIssuePage'),
    ),
    helpTopic: 'com-group-compliance-ilo-issue-tt',
  },
  '/groups': {
    component: lazy(() => import('../components/GroupsPage/GroupsPage')),
    helpTopic: 'com-groups-tt',
  },
  '/hardwareDetail/:applianceId/:hardwareId': {
    component: lazy(
      () => import('../components/HardwareDetailPage/HardwareDetails'),
    ),
    helpTopic: 'com-hwdetail-app-hw-tt',
  },
  '/inventory': {
    component: lazy(() => import('../components/InventoryPage/InventoryPage')),
    helpTopic: 'com-inventory-tt',
  },
  '/manage': {
    component: lazy(() => import('../components/ManagePage/ManagePage')),
    helpTopic: 'com-manage-tt',
  },
  '/metricsData': {
    component: lazy(
      () =>
        import('../components/MetricsDataCollection/MetricsDataCollectionPage'),
    ),
    helpTopic: 'com-metrics-dc-tt',
  },
  '/overview': {
    component: lazy(
      () => import('../components/OverviewPage/OverviewPagePicker'),
    ),
    helpTopic: 'com-overview-tt',
  },
  '/policyDetail/:id': {
    component: lazy(
      () => import('../components/ApprovalPolicyPage/ApprovalPolicyDetailPage'),
    ),
    helpTopic: DEFAULT_TOPIC, // TODO - get topic ID from Susan
  },
  '/reports/carbonFootprint/:id': {
    component: lazy(
      () => import('../components/ReportDetailPage/ReportDetailPageV2'),
    ),
    helpTopic: 'com-sustainability-report-tt',
  },
  '/reports/serverHardwareInventory/:id': {
    component: lazy(
      () =>
        import(
          '../components/ReportServerHwInventoryDetailPage/ReportServerHwInventoryDetailPage'
        ),
    ),
    helpTopic: 'com-hardware-inventory-report-tt',
  },
  '/reports/serverHardwareInventory': {
    component: lazy(
      () =>
        import(
          '../components/ReportServerHwInventoryDetailPage/ReportServerHwInventoryDetailPage'
        ),
    ),
    helpTopic: 'com-hardware-inventory-report-tt',
    ldFlags: ['T_20250709_89486_SERVER_HARDWARE_REPORT_NEW_UI_BOOL'],
  },
  '/reports': {
    component: ReportsPage,
    helpTopic: 'com-analytics-tt',
    exact: true,
  },
  '/serverDetail/:id': {
    component: lazy(
      () => import('../components/DeviceDetailPage/DeviceDetailPage'),
    ),
    helpTopic: 'com-serverdetail-tt',
  },
  '/serverProfileDetail/:applianceId/:serverProfileId': {
    component: lazy(
      () => import('../components/ServerProfile/ServerProfileDetails'),
    ),
    helpTopic: DEFAULT_TOPIC, // TODO - get topic ID from Susan
    ldFlags: ['T_20230524_8648_SERVER_PROFILES_UI_BOOL'],
  },
  '/serverProfile': {
    component: lazy(
      () => import('../components/ServerProfile/ServerProfilePage'),
    ),
    helpTopic: DEFAULT_TOPIC, // TODO - get topic ID from Susan
    exact: true,
    ldFlags: ['T_20230524_8648_SERVER_PROFILES_UI_BOOL'],
  },
  '/servers': {
    component: lazy(() => import('../components/DevicesPage/DevicesPage')),
    helpTopic: 'com-servers-tt',
    isSessionStorageEnabledForPage: true,
  },
  '/serviceNow': {
    component: lazy(
      () =>
        import('../components/ServiceNowIntegration/ServiceNowIntegrationPage'),
    ),
    helpTopic: 'com-servicenow-tt',
  },
  '/settingDetail/:id': {
    component: lazy(
      () =>
        import(
          '../components/ServerSettingsDetailPage/ServerSettingsDetailPage'
        ),
    ),
    helpTopic: 'com-setting-details-tt',
  },
  '/settings': {
    component: lazy(
      () => import('../components/ServerSettingsPage/ServerSettingsPage'),
    ),
    helpTopic: 'com-settings-tt',
  },
  '/vcenterDetail/:id': {
    component: lazy(
      () => import('../components/VcenterPage/VcenterDetailPage'),
    ),
    helpTopic: 'com-vcenter-details-tt',
    ldFlags: ['T_20240918_44812_COM_VCENTER_INTEG_UI_BOOL'],
  },
  '/vsphereIntegration': {
    component: lazy(
      () => import('../components/VsphereIntegration/VsphereIntegrationPage'),
    ),
    helpTopic: 'com-vsphere-tt',
    ldFlags: ['T_20240918_44812_COM_VCENTER_INTEG_UI_BOOL'],
  },
};

export default ROUTE_CONFIG;

// returns the path config only if:
// - it is marked as having complex LD flags (anything other than ||'ing all of the flags together)
// - there are no ld flags associated and not marked as having complex LD flags
// - all of the ld flags are enabled
export const getRouteConfig = (path, flags) => {
  const pathConfig = ROUTE_CONFIG?.[path];

  if (!pathConfig) return null;

  const { ldFlags = [], complexLDFlag } = pathConfig;

  if (complexLDFlag) return pathConfig;

  // the equivalent of ||'ing all of the flags together, stop as soon as one is true
  const isEnabled =
    ldFlags && ldFlags.length > 0
      ? ldFlags.some(
          flagName =>
            // eslint-disable-next-line indigo/flags-must-have-defaults
            !!flags?.[flagName]?.(),
        )
      : true;

  return isEnabled ? pathConfig : null;
};
